import styles from './selectStories.module.scss';
import { IWhatIfStory } from 'src/core/repo/whatif/types';
import { useRef, useState } from 'react';
import { useEffect } from 'react';
import { GoChevronLeft } from 'react-icons/go';
import axios from 'axios';
import RecordingVoice from '../recordingVoice/RecordingVoice';
import StartRecording from '../startRecording/StartRecording';
import GeneratedStory from '../generatedStory/GeneratedStory';
import emptyImage from '@assets/images/ai4-plan.png';

interface Props {
  userStoryText: string;
  setUserStoryText: (text: string) => void;
  setStep: (step: number) => void;
  story: IWhatIfStory;
  setFile: React.Dispatch<React.SetStateAction<File | null>>;
}

const SelectedStory = ({
  setUserStoryText,
  userStoryText,
  setStep,
  story,
  setFile,
}: Props) => {
  const TOTAL_TIME_SECOND = 10 * 60;

  const [isRecording, setIsRecording] = useState(false);
  const [audioUrl, setAudioUrl] = useState('');
  const [timer, setTimer] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const intervalId = useRef<NodeJS.Timeout | null>(null);
  const [activeStory, setActiveStory] = useState<null | number>(null);
  const [finishedStoryId, setFinishedStoryId] = useState<null | number>(null);

  const mediaRecorder = useRef<MediaRecorder | null>(null);
  const audioChunks = useRef<Blob[]>([]);
  const animationId = useRef<number | null>(null);

  const startRecording = async (story: IWhatIfStory) => {
    // Reset state for the selected item (if needed, implement state management here)
    setActiveStory(story.StoryID);
    setFinishedStoryId(null);
    setTimer(0);
    setAudioUrl('');
    setUserStoryText('');
    setIsRecording(true);
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const recorder = new MediaRecorder(stream);
    mediaRecorder.current = recorder;
    audioChunks.current = [];

    recorder.ondataavailable = (event) => {
      audioChunks.current.push(event.data);
    };

    recorder.onstop = async () => {
      const blob = new Blob(audioChunks.current, { type: 'audio/webm' });
      const url = URL.createObjectURL(blob);
      setAudioUrl(url);
      // تبدیل Blob به Base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64data = reader.result?.toString().split(',')[1];
        if (base64data) {
          await sendVoice(base64data);
        }
      };
      reader.readAsDataURL(blob);
    };

    const sendVoice = async (data: string) => {
      setIsGenerating(true);
      // ارسال Base64 به بک‌اند
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_BASE_URL}voice-to-text`,
          { file: data },
          {
            headers: {
              'Content-Type': 'application/json',
            },
          },
        );
        if (response.status === 200) {
          if (response.data) {
            setUserStoryText(response.data.text as string);
            setIsGenerating(false);
          }
        }
      } catch (error) {
        console.error('Upload error:', error);
      }
    };

    recorder.start();
    return stream;
  };

  const stopRecording = () => {
    if (mediaRecorder.current && mediaRecorder.current.state !== 'inactive') {
      mediaRecorder.current.stop();
    }
    if (animationId.current) {
      cancelAnimationFrame(animationId.current);
    }
    setIsRecording(false);
    setFinishedStoryId(activeStory);
    setActiveStory(null);
  };

  // Timer effect: when isRecording is true, start interval, else clear it
  useEffect(() => {
    if (isRecording) {
      intervalId.current = setInterval(() => {
        setTimer((prev) => prev + 1);
      }, 1000);
    } else {
      if (intervalId.current) {
        clearInterval(intervalId.current);
        intervalId.current = null;
      }
    }
    return () => {
      if (intervalId.current) {
        clearInterval(intervalId.current);
        intervalId.current = null;
      }
    };
  }, [isRecording]);

  // Auto-stop when timer reaches max
  useEffect(() => {
    if (isRecording && timer >= TOTAL_TIME_SECOND) {
      stopRecording();
    }
  }, [timer, isRecording]);

  const handleRestRecording = () => {
    setIsRecording(true);
    setAudioUrl('');
    setIsGenerating(false);
    setUserStoryText('');
  };

  return (
    <div className="container">
      <div className={styles.description_wrapper}>
        <button onClick={() => setStep(4)} className={styles.back_button}>
          <GoChevronLeft size={22} />
          <p>Back</p>
        </button>
      </div>
      <div className={styles.storiesWrapper}>
        <div className={styles.story_card}>
          <div className={styles.story_card_image}>
            <img
              style={{ objectFit: story.StoryImageURL ? 'cover' : 'contain' }}
              src={story.StoryImageURL ? story.StoryImageURL : emptyImage}
              alt={story.StoryWhatIf}
            />
          </div>
          {activeStory === story.StoryID ? (
            isRecording && !audioUrl ? (
              <RecordingVoice
                isGenerating={isGenerating}
                stopRecording={stopRecording}
                timer={timer}
                total={TOTAL_TIME_SECOND}
              />
            ) : null
          ) : finishedStoryId === story.StoryID && audioUrl ? (
            <GeneratedStory
            story={story}
              audioUrl={audioUrl}
              handleRestRecording={handleRestRecording}
              setStep={setStep}
              isGenerating={isGenerating}
              userStoryText={userStoryText}
              setUserStoryText={setUserStoryText}
            />
          ) : (
            <StartRecording
              story={story}
              setFile={setFile}
              startRecording={() => {
                void startRecording(story);
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default SelectedStory;
